import { useContractInvoicesQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import { useUpdateSalesContractMutation } from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract, SalesContractFormData } from "@/features/dashboard/types/sales-contract";
import type React from "react";
import { toast } from "sonner";
import { SalesContractForm } from "./sales-contract-form";

interface EditContractModalProps {
	contract: SalesContract;
	onSuccess?: () => void;
}

export const EditContractModal: React.FC<EditContractModalProps> = ({ contract, onSuccess }) => {
	const updateMutation = useUpdateSalesContractMutation();

	// Fetch existing invoices for this contract
	const { data: invoicesData, isLoading: invoicesLoading } = useContractInvoicesQuery(contract.id);

	const handleSubmit = async (data: SalesContractFormData) => {
		try {
			await updateMutation.mutateAsync({ id: contract.id, data });

			// Show different success messages based on contract type
			if (data.monthly_recurring_sales > 0 && data.payment_type) {
				toast.success("Recurring contract updated successfully with missing invoices auto-generated");
			} else {
				toast.success("One-time contract updated successfully with missing invoices auto-generated");
			}

			onSuccess?.();
		} catch (error) {
			// Error is handled by the mutation
		}
	};

	// Show loading state if invoices are still being fetched
	if (invoicesLoading) {
		return (
			<div className="flex items-center justify-center p-6">
				<div className="flex items-center gap-2">
					<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
					<span className="text-sm text-gray-600">Loading contract invoices...</span>
				</div>
			</div>
		);
	}

	// Convert existing invoices to the format expected by the form
	const existingInvoices =
		invoicesData?.data?.map((invoice, index) => ({
			id: `existing-${invoice.id}`,
			sequence: index + 1,
			amount: Number.parseFloat(invoice.amount.toString()),
			description: invoice.description || "",
			invoiceDate: invoice.invoice_date,
			dueDate: invoice.due_date,
			startDate: invoice.start_date,
			endDate: invoice.start_date, // Use start_date as fallback for endDate if not available
			existingInvoiceId: invoice.id, // Keep reference to existing invoice ID
		})) || [];

	// Use actual invoice count instead of stored contract value
	const actualInvoiceCount = existingInvoices.length || contract.number_of_invoices || 1;

	const initialData: SalesContractFormData = {
		project_id: contract.project_id,
		contract_amount: contract.contract_amount,
		monthly_recurring_sales: contract.monthly_recurring_sales,
		payment_type: contract.payment_type,
		project_type: contract.project_type || "revlv_project",
		royalty_amount: contract.royalty_amount,
		original_contract_amount: contract.original_contract_amount,
		original_monthly_recurring_sales: contract.original_monthly_recurring_sales,
		number_of_invoices: actualInvoiceCount,
		start_date: contract.start_date,
		end_date: contract.end_date,
		notes: contract.notes || "",
		tags: contract.tags || [],
		is_vatable: contract.is_vatable ?? true,
	};

	// Prepare the project data for the combobox
	const projectData = contract.project
		? {
				id: contract.project.id,
				name: contract.project.name,
				category: contract.project.category,
				client: contract.project.client,
			}
		: undefined;

	return (
		<SalesContractForm
			initialData={initialData}
			projectData={projectData}
			onSubmit={handleSubmit}
			isLoading={updateMutation.isPending}
			existingInvoices={existingInvoices}
		/>
	);
};
